/**
 * ChangeReason — the "why" every admin write carries in x-pcb-change-reason
 * (docs/architecture/admin-portal-shape.md §6). Each API refuses a write (400) without one,
 * and records it with who, when, before and after.
 *
 * Header values must be plain bytes. pcb-retail-api URL-decodes the header, so Retail calls
 * send encodeURIComponent(reason). Other APIs (the Hub) read it as sent, so for them the
 * reason is normalised to printable ASCII (curly quotes and dashes straightened) and any
 * other character is refused before the request.
 */

import { TextArea } from '@/components/FormField';

export const REASON_MIN = 5;
export const REASON_MAX = 500;

/** Straighten typographic punctuation so a pasted reason survives as an ASCII header. */
export function asciiReason(reason: string): string {
  return reason
    .replace(/[‘’‚′]/g, "'")
    .replace(/[“”„″]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/…/g, '...')
    .replace(/[   ]/g, ' ')
    .replace(/[\r\n\t]+/g, ' ')
    .trim();
}

/** Null when the reason can be sent; otherwise what to tell the person. */
export function reasonProblem(reason: string, opts: { asciiOnly?: boolean } = {}): string | null {
  const r = opts.asciiOnly ? asciiReason(reason) : reason.trim();
  if (r.length < REASON_MIN) return `Say why you are making this change (at least ${REASON_MIN} characters).`;
  if (r.length > REASON_MAX) return `Keep the reason to ${REASON_MAX} characters.`;
  // eslint-disable-next-line no-control-regex
  if (opts.asciiOnly && /[^\x20-\x7E]/.test(r)) return 'Use plain letters, digits and punctuation in the reason.';
  return null;
}

/** The header value for pcb-retail-api (URL-encoded; the API decodes it). */
export function retailReasonHeader(reason: string): Record<string, string> {
  return { 'x-pcb-change-reason': encodeURIComponent(reason.trim()) };
}

export function ReasonField({
  value,
  onChange,
  showError,
  asciiOnly,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Show the problem (after a save attempt). */
  showError?: boolean;
  asciiOnly?: boolean;
}): JSX.Element {
  const problem = reasonProblem(value, { asciiOnly });
  return (
    <TextArea
      label="Reason for this change"
      required
      value={value}
      onChange={onChange}
      hint="Recorded with your name, the time, and the before and after values."
      error={showError && problem ? problem : undefined}
    />
  );
}
